import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateString } from '../../../../../common/src/date_string.ts';
import {
  IntervalType,
  Org,
  OrgType,
  RepeatKind,
  RowId,
  SongListEntryType,
  SongListSchemaEntry,
} from '../../../../../common/src/model.ts';
import { getWeeklySongListType, isBlankEntry } from '../../../../../common/src/model_util.ts';
import { getWeeklyDateForOrganization } from '../../../../../common/src/org.ts';
import { getWriteDownBeforeRowChange } from '../../../../../common/src/passed_sundays.ts';
import { canShowAsHymnOfTheMonth, getRepeatKind } from '../../../../../common/src/repeating_rows.ts';
import { capitalize, ensureUnreachable } from '../../../../../common/src/util.ts';
import BibleIcon from '../../../assets/bible.svg?react';
import ChevronRightIcon from '../../../assets/chevron-right-small.svg?react';
import EighthNoteIcon from '../../../assets/eighth-note.svg?react';
import PersonIcon from '../../../assets/person.svg?react';
import TrashIcon from '../../../assets/trash.svg?react';
import WedgeDownIcon from '../../../assets/wedge-down.svg?react';
import WedgeUpIcon from '../../../assets/wedge-up.svg?react';
import { LocalStorageKey, usabilityTooltipDisplayCount } from '../../../data/client_local_storage.ts';
import { getWrittenThroughForOrg } from '../../../data/passed_sundays_written_through.ts';
import { useCurrentOrg } from '../../../data/use_current_org.tsx';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { useUserAttributes } from '../../../data/use_user_attributes.ts';
import { CheckBox } from '../../../util/check_box.tsx';
import { confirm } from '../../../util/confirm.tsx';
import { CheckMark, focusNextTabStop } from '../../../util/shared.tsx';
import { SpeechBubble, SpeechBubbleOrientation } from '../../../util/speech_bubble.tsx';
import { useCurrentDate, useReaderInstant } from '../../../util/use_current_date.tsx';
import { useEffectOnce } from '../../../util/use_effect_once.ts';
import { writePlannerWrites } from './planner_edits.ts';
import { opensUpwardCss, useOpensUpward } from './popup.tsx';
import { getRepeatKindLabel, HymnOfTheMonthStarIcon, RepeatGlyphs, RepeatKindIcon } from './repeat_kind_ui.tsx';
import {
  DateRowTop,
  DisplayUnauthorizedAlert,
  Menu,
  MenuEntry,
  PlannerYearHeight,
  SongCellPadding,
  SongRowHeightDouble,
  SongRowHeightSingle,
  SongRowWidth,
} from './shared.tsx';
import {
  setSongListSchemaLabel,
  setSongListSchemaRepeat,
  setSongListSchemaRowAssigned,
  setSongListSchemaShowAsHymnOfTheMonth,
  setSongListSchemaType,
} from './song_list_schema.ts';
import { AllSongLists } from './use_all_song_lists.ts';

interface Props {
  allSongLists: AllSongLists;
  songRowCount: number;
}

export function PlannerSchemaColumn({ allSongLists, songRowCount }: Props) {
  const { currentOrg } = useCurrentOrg();
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const [newRowIndex, setNewRowIndex] = useState<number | undefined>(); // for selecting new rows
  return (
    <PlannerSchemaWrapper>
      <YearRow />
      <WeekRow>{getSchemaColumnTitle(currentOrg)}</WeekRow>
      {currentOrg?.type !== OrgType.School && (
        <SongSchemaRow
          key="hymn-of-the-month"
          rowIndex={-1}
          allSongLists={allSongLists}
          newRowIndex={newRowIndex}
          setNewRowIndex={setNewRowIndex}
          isInternalUser={isInternalUser}
          isOrgAdmin={isOrgAdmin}
        />
      )}
      {
        // TODO(hewitt): Using the index as the React key means the schema rows slide around underneath the React
        //               components, rather than properly inserting and removing React components for each row.
        //               Really, schema rows should have unique identifiers which would be used as React keys,
        //               then React would properly track schema row <-> DOM element.
        //               We can get away with this for now because the SongSchemaRow DOM element only relies
        //               on the index and contains a hack-job useEffect() to slide row names up/down.
        [...Array(songRowCount).keys()].map((index) => (
          <SongSchemaRow
            key={index + 1}
            rowIndex={index + 1}
            allSongLists={allSongLists}
            newRowIndex={newRowIndex}
            setNewRowIndex={setNewRowIndex}
            isOrgAdmin={isOrgAdmin}
            isInternalUser={isInternalUser}
          />
        ))
      }
    </PlannerSchemaWrapper>
  );
}

const PlannerSchemaWrapper = styled.div`
  z-index: 5; // above liturgy row content, but below year
  display: flex;
  flex-direction: column;
  position: sticky;
  left: 0;
`;

const YearRow = styled.div`
  height: ${PlannerYearHeight};
  position: sticky;
  top: 0;
  background-color: var(--color-background);
`;

function SongSchemaRow({
  allSongLists,
  rowIndex,
  newRowIndex,
  setNewRowIndex,
  isOrgAdmin,
  isInternalUser,
}: {
  allSongLists: AllSongLists;
  rowIndex: number;
  newRowIndex: number | undefined;
  setNewRowIndex: (newRowIndex: number | undefined) => void;
  isOrgAdmin: boolean;
  isInternalUser: boolean;
}) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const schema = currentOrg?.churchLiturgySchema;
  const row = schema?.rows.find(({ row }) => row === rowIndex);
  const isHymnOfTheMonth = rowIndex < 0;
  const labelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (rowIndex === newRowIndex) {
      labelInputRef.current?.focus();
      setNewRowIndex(undefined);
    }
  }, [labelInputRef, rowIndex, newRowIndex, setNewRowIndex]);

  useEffect(() => {
    // TODO(hewitt): Total hack-job. Schema rows should have unique IDs.
    //               See comment above where <SongSchemaRow> is instantiated with the index for a key.
    if (labelInputRef.current) {
      labelInputRef.current.value = row?.label ?? '';
    }
  }, [row?.label]);

  const onLabelBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!currentOrg) {
        return;
      }
      const label = event.target.value && event.target.value !== '' ? event.target.value : undefined;
      if (label !== row?.label) {
        void updateOrgInfo({ id: currentOrg.id, churchLiturgySchema: setSongListSchemaLabel(schema, rowIndex, label) });
      }
    },
    [currentOrg, row?.label, rowIndex, schema, updateOrgInfo],
  );

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    // Treat <Enter> keypress like <Tab>
    if (event.key === 'Enter') {
      focusNextTabStop(event.target as HTMLInputElement);
    }
  }, []);

  const onMouseDown = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      if (!isOrgAdmin && !isInternalUser) {
        void DisplayUnauthorizedAlert();
        event.preventDefault();
        return;
      }
    },
    [isOrgAdmin, isInternalUser],
  );

  return (
    <SongRowWrapper $isDoubleHeight={isHymnOfTheMonth}>
      <RowCellWrapper key="cell">
        {isHymnOfTheMonth ? (
          'Hymn of the Month'
        ) : (
          <RowInput
            defaultValue={row?.label}
            onMouseDown={onMouseDown}
            onBlur={onLabelBlur}
            onKeyDown={onKeyDown}
            placeholder={
              currentOrg?.type === OrgType.School ? getPlaceholderFromInterval(row?.intervalType) : undefined
            }
            ref={labelInputRef}
          />
        )}
      </RowCellWrapper>
      {!isHymnOfTheMonth &&
        (currentOrg?.type === OrgType.School ? (
          <AssignmentIcon rowIndex={rowIndex} isChecked={!!row?.assigned} />
        ) : (
          <RowIconsWrapper key="icon">
            {currentOrg?.type === OrgType.Church && <RepeatGlyphs row={row} />}
            <TypeIcon
              allSongLists={allSongLists}
              row={row}
              type={row?.type ?? SongListEntryType.Song}
              rowIndex={rowIndex}
              setNewRowIndex={setNewRowIndex}
              isOrgAdmin={isOrgAdmin}
              isInternalUser={isInternalUser}
            />
          </RowIconsWrapper>
        ))}
    </SongRowWrapper>
  );
}

const SongRowWrapper = styled.div<{ $isDoubleHeight?: boolean }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  height: ${(props) => (props.$isDoubleHeight ? SongRowHeightDouble : SongRowHeightSingle)};
  width: ${SongRowWidth};
  outline: 1px solid var(--color-text); /* outline instead of border takes up 0 space */
  background-color: var(--color-planner-row-header);
  padding: ${SongCellPadding};
  display: flex;
  align-items: center;
`;

const WeekRow = styled(SongRowWrapper)`
  position: sticky;
  top: ${DateRowTop};
`;

const RowCellWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  text-align: left;
`;

// the repeat glyphs and the type icon, on the right of the row header
const RowIconsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const TypeIconWidth = '15px';
const TypeIconHeight = '18px';

// fills the row header beside the icons, shrinking when repeat glyphs appear
const RowInput = styled.input`
  background-color: transparent;
  border: none;
  flex: 1;
  min-width: 0;
`;

function TypeIcon({
  allSongLists,
  row,
  rowIndex,
  setNewRowIndex,
  type,
  isOrgAdmin,
  isInternalUser,
}: {
  allSongLists: AllSongLists;
  row: SongListSchemaEntry | undefined;
  rowIndex: number;
  setNewRowIndex: (newRowIndex: number) => void;
  type: SongListEntryType;
  isOrgAdmin: boolean;
  isInternalUser: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { ref: menuRef, isUpward: isMenuUpward } = useOpensUpward('below');
  // only churches repeat rows (issue #4170): the fill that shows a repeating row is church-only
  const isChurch = useCurrentOrg().currentOrg?.type === OrgType.Church;

  const onClick = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      if (!isOrgAdmin && !isInternalUser) {
        void DisplayUnauthorizedAlert();
        event.preventDefault();
        return;
      }
      setMenuOpen(!menuOpen);
    },
    [isOrgAdmin, isInternalUser, menuOpen],
  );

  const onBlur = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <TypeIconWrapper
      role="button"
      aria-label={row?.label ? `${row.label} row menu` : 'Row menu'}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      tabIndex={rowIndex + 1000}
      onClick={onClick}
      onBlur={onBlur}
    >
      {getTypeIcon(type)}
      {menuOpen && (
        <RowMenu ref={menuRef} $opensUpward={isMenuUpward}>
          {Object.values(SongListEntryType).map((item) => (
            <TypeMenuEntry key={item} type={item} rowIndex={rowIndex} />
          ))}
          {isChurch && (
            <RepeatsMenuEntry
              allSongLists={allSongLists}
              row={row}
              rowIndex={rowIndex}
              isRowMenuUpward={isMenuUpward}
            />
          )}
          <InsertMenuEntry
            allSongLists={allSongLists}
            isBefore={true}
            rowIndex={rowIndex}
            setNewRowIndex={setNewRowIndex}
          />
          <InsertMenuEntry
            allSongLists={allSongLists}
            isBefore={false}
            rowIndex={rowIndex}
            setNewRowIndex={setNewRowIndex}
          />
          <DeleteRow allSongLists={allSongLists} rowIndex={rowIndex} />
        </RowMenu>
      )}
    </TypeIconWrapper>
  );
}

function AssignmentIcon({ rowIndex, isChecked }: { rowIndex: RowId; isChecked: boolean }) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const [tooltipCount, setTooltipCount] = useLocalStorage(LocalStorageKey.TooltipVisibilityCount);

  const onClick = useCallback(() => {
    if (!currentOrg) {
      return;
    }
    const schema = currentOrg?.churchLiturgySchema;
    void updateOrgInfo({
      id: currentOrg.id,
      churchLiturgySchema: setSongListSchemaRowAssigned(schema, rowIndex, !isChecked),
    });
  }, [currentOrg, isChecked, rowIndex, updateOrgInfo]);

  useEffectOnce(() => {
    if (rowIndex !== 1) {
      return;
    }
    setTooltipCount((tooltipCount) =>
      (tooltipCount.schoolAssignment ?? 0) < usabilityTooltipDisplayCount + 1
        ? { ...tooltipCount, schoolAssignment: (tooltipCount.schoolAssignment ?? 0) + 1 }
        : tooltipCount,
    );
  });

  return (
    <CheckBox onClick={onClick} isChecked={isChecked}>
      {rowIndex === 1 && (tooltipCount.schoolAssignment ?? 0) <= usabilityTooltipDisplayCount && (
        <SchoolAssignmentSpeechBubble $orientation={SpeechBubbleOrientation.Horizontal}>
          <SchoolAssignmentTooltip>Prioritize your Family Singing Plans</SchoolAssignmentTooltip>
        </SchoolAssignmentSpeechBubble>
      )}
    </CheckBox>
  );
}

function InsertMenuEntry({
  allSongLists,
  isBefore,
  rowIndex,
  setNewRowIndex,
}: {
  allSongLists: AllSongLists;
  isBefore: boolean;
  rowIndex: number;
  setNewRowIndex: (newRowIndex: number) => void;
}) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const today = useCurrentDate();
  const getReaderNow = useReaderInstant();
  const schema = currentOrg?.churchLiturgySchema;

  // Insert Row first writes down the Sundays begun, under the schema as it stands, as Delete Row does (issue #4209):
  // the shift then moves every row's written-down entries with their row numbers, and a passed Sunday's blanks stand
  // where the write-down moved them, rather than being dropped from the list the shift rewrites
  const onInsertRow = useCallback(async () => {
    const newRowIndex = isBefore ? rowIndex : rowIndex + 1;
    if (currentOrg) {
      await writeDownSundaysBegun(currentOrg, allSongLists, { today, now: getReaderNow() });
    }
    if (currentOrg && schema && schema.rows.length > 0) {
      await updateOrgInfo({
        id: currentOrg.id,
        churchLiturgySchema: {
          ...schema,
          rows: schema.rows.map((row) => (row.row >= newRowIndex ? { ...row, row: row.row + 1 } : row)),
        },
      });
    }
    await allSongLists.insertNewRowAt(newRowIndex);
    setNewRowIndex(newRowIndex);
  }, [allSongLists, currentOrg, getReaderNow, isBefore, rowIndex, schema, setNewRowIndex, today, updateOrgInfo]);

  return (
    <MenuEntry $isFirst={false} $isLast={false} $borderTop={isBefore} $borderBottom={!isBefore} onClick={onInsertRow}>
      {isBefore ? <InsertBeforeIconWrapper /> : <InsertAfterIconWrapper />}
      Insert {isBefore ? 'Before' : 'After'}
    </MenuEntry>
  );
}

// positioned, so a row menu that opens upward can end at its top
const TypeIconWrapper = styled.div`
  position: relative;
`;

// hangs from the bottom of the type icon (what useOpensUpward measures from), or ends above it near the bottom of the
// planner
const RowMenu = styled(Menu)<{ $opensUpward: boolean }>`
  top: 100%;
  ${(props) => props.$opensUpward && opensUpwardCss('below')}
`;

const TypeMenuEntry = function ({ type, rowIndex }: { type: SongListEntryType; rowIndex: number }) {
  const entryTypes = Object.values(SongListEntryType);
  const isFirst = entryTypes.indexOf(type) === 0;
  const isLast = entryTypes.indexOf(type) === entryTypes.length - 1;
  const { currentOrg, updateOrgInfo } = useCurrentOrg();

  const onClick = useCallback(() => {
    if (!currentOrg) {
      return;
    }
    const schema = currentOrg?.churchLiturgySchema;
    void updateOrgInfo({ id: currentOrg.id, churchLiturgySchema: setSongListSchemaType(schema, rowIndex, type) });
  }, [currentOrg, rowIndex, type, updateOrgInfo]);

  return (
    <MenuEntry $isFirst={isFirst} $isLast={false} $borderBottom={isLast} onClick={onClick}>
      {getTypeIcon(type)}
      {capitalize(type)}
    </MenuEntry>
  );
};

// how long the Repeats flyout stays open after the pointer leaves it, so a diagonal move from the Repeats line to a
// lower line of the flyout, which crosses the entries below Repeats, does not close it on the way
const repeatsFlyoutCloseDelayMs = 300;

// The Repeats line of the row menu (issue #4170) and the flyout beside it holding the repeat kinds and, for a Song-type
// Same-all-month row, Show as Hymn of the Month. A choice changes the schema row and, beyond writing down the Sundays
// begun (issue #4192), no song list (issue #4191). Hovering or clicking the line opens the flyout; the flyout closes
// with the row menu, which unmounts this.
function RepeatsMenuEntry({ allSongLists, row, rowIndex, isRowMenuUpward }: RepeatsFlyoutProps) {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = undefined;
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  const onMouseEnter = useCallback(() => {
    cancelClose();
    setIsFlyoutOpen(true);
  }, [cancelClose]);

  const onMouseLeave = useCallback(() => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => setIsFlyoutOpen(false), repeatsFlyoutCloseDelayMs);
  }, [cancelClose]);

  // opens rather than toggles: a hover (or the hover a tap emulates) has usually opened the flyout already
  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation(); // keep the row menu open
      cancelClose();
      setIsFlyoutOpen(true);
    },
    [cancelClose],
  );

  return (
    <RepeatsEntryWrapper onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <MenuEntry
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={isFlyoutOpen}
        $isFirst={false}
        $isLast={false}
        $borderTop={true}
        $borderBottom={true}
        onClick={onClick}
      >
        <RepeatKindIcon kind={getRepeatKind(row)} />
        Repeats
        <ChevronIconWrapper aria-hidden={true} />
      </MenuEntry>
      {isFlyoutOpen && (
        <RepeatsFlyout allSongLists={allSongLists} row={row} rowIndex={rowIndex} isRowMenuUpward={isRowMenuUpward} />
      )}
    </RepeatsEntryWrapper>
  );
}

interface RepeatsFlyoutProps {
  allSongLists: AllSongLists;
  row: SongListSchemaEntry | undefined;
  rowIndex: number;
  // whether the row menu holding the Repeats line opens upward: when it flips, the line moves and the flyout is
  // measured again
  isRowMenuUpward: boolean;
}

function RepeatsFlyout({ allSongLists, row, rowIndex, isRowMenuUpward }: RepeatsFlyoutProps) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const today = useCurrentDate();
  const getReaderNow = useReaderInstant();
  const currentKind = getRepeatKind(row);
  // offered only on a Song-type Same-all-month row; checked whenever the row carries the flag, which it keeps through a
  // switch of kind or type
  const isShowAsHymnOfTheMonthVisible = canShowAsHymnOfTheMonth(row);
  const isShownAsHymnOfTheMonth = !!row?.showAsHymnOfTheMonth;
  const kinds = Object.values(RepeatKind);
  const { ref: flyoutRef, isUpward } = useOpensUpward('beside', isRowMenuUpward);

  // The switch first writes down the Sundays begun, under the schema as it stands (issue #4192): a passed Sunday the
  // server has not written yet, and today when it is a Sunday, keep what they showed whatever the new kind. Then it
  // writes the schema, the new kind applying from the admin's current Sunday (repeatFrom). It changes no list beyond
  // that write-down, which records what the Sundays already showed: the row's songs stay in the Sundays' lists, which
  // the repeat kind only shows and edits differently (issue #4191). Choosing the kind the row has does nothing, so
  // repeatFrom moves only on a real switch. Lets the click bubble to the type icon, which closes the row menu.
  const onChooseKind = useCallback(
    async (kind: RepeatKind) => {
      if (!currentOrg || kind === currentKind) {
        return;
      }
      await writeDownSundaysBegun(currentOrg, allSongLists, { today, now: getReaderNow() });
      const schema = currentOrg.churchLiturgySchema;
      const currentSunday = getWeeklyDateForOrganization(currentOrg, today);
      await updateOrgInfo({
        id: currentOrg.id,
        churchLiturgySchema: setSongListSchemaRepeat(schema, rowIndex, kind, currentSunday),
      });
    },
    [allSongLists, currentKind, currentOrg, getReaderNow, rowIndex, today, updateOrgInfo],
  );

  const onToggleShowAsHymnOfTheMonth = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation(); // keep the row menu open
      if (!currentOrg) {
        return;
      }
      const schema = currentOrg.churchLiturgySchema;
      void updateOrgInfo({
        id: currentOrg.id,
        churchLiturgySchema: setSongListSchemaShowAsHymnOfTheMonth(schema, rowIndex, !isShownAsHymnOfTheMonth),
      });
    },
    [currentOrg, isShownAsHymnOfTheMonth, rowIndex, updateOrgInfo],
  );

  return (
    <RepeatsFlyoutMenu ref={flyoutRef} role="menu" aria-label="Repeats" $opensUpward={isUpward}>
      {kinds.map((kind, index) => (
        <MenuEntry
          key={kind}
          role="menuitemradio"
          aria-checked={kind === currentKind}
          $isFirst={index === 0}
          $isLast={index === kinds.length - 1 && !isShowAsHymnOfTheMonthVisible}
          onClick={() => void onChooseKind(kind)}
        >
          <RepeatKindIcon kind={kind} />
          {getRepeatKindLabel(kind)}
          {kind === currentKind && (
            <CheckMark aria-hidden={true} $size="14px" $padding="0" $fillColor="var(--color-nightingale)" />
          )}
        </MenuEntry>
      ))}
      {isShowAsHymnOfTheMonthVisible && (
        <MenuEntry
          role="menuitemcheckbox"
          aria-checked={isShownAsHymnOfTheMonth}
          $isFirst={false}
          $isLast={true}
          $borderTop={true}
          onClick={onToggleShowAsHymnOfTheMonth}
        >
          <HymnOfTheMonthStarIcon />
          Show as Hymn of the Month
          <CheckBoxWrapper>
            {/* the whole line toggles, so the box itself does nothing more */}
            <CheckBox onClick={() => undefined} isChecked={isShownAsHymnOfTheMonth} />
          </CheckBoxWrapper>
        </MenuEntry>
      )}
    </RepeatsFlyoutMenu>
  );
}

const RepeatsEntryWrapper = styled.div`
  position: relative;
`;

// beside the Repeats line, from its top down, or near the bottom of the planner from its bottom up
const RepeatsFlyoutMenu = styled(Menu)<{ $opensUpward: boolean }>`
  left: 100%;
  top: 0;
  ${(props) => props.$opensUpward && opensUpwardCss('beside')}
`;

const ChevronIconWrapper = styled(ChevronRightIcon)`
  width: 16px;
  height: 16px;
  margin-left: auto;
  color: var(--color-text);
`;

const CheckBoxWrapper = styled.div`
  margin-left: auto;
  padding-left: 10px;
`;

const TypeTextWrapper = styled.div`
  font-family: 'Times New Roman', Times, serif;
  font-size: 1.2rem;
  font-weight: bold;
  width: ${TypeIconWidth};
  cursor: pointer;
`;

const EighthNoteIconWrapper = styled(EighthNoteIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

const BibleIconWrapper = styled(BibleIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

const PersonIconWrapper = styled(PersonIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

function getTypeIcon(type: SongListEntryType) {
  switch (type) {
    case SongListEntryType.Song:
      return <EighthNoteIconWrapper />;
    case SongListEntryType.Assignment:
      return <PersonIconWrapper />;
    case SongListEntryType.Scripture:
      return <BibleIconWrapper />;
    case SongListEntryType.Text:
      return <TypeTextWrapper>T</TypeTextWrapper>;
    default:
      ensureUnreachable(type);
  }
}

// Before a change to a church row that could alter what a Sunday shows (a repeat switch, Delete Row, Insert Row),
// writes down the Sundays begun on `today` that the server has not written yet, under the schema as it stands, so the
// outcome never depends on timing (issue #4192): every passed Sunday after the one the server has written the church
// down through, as the planner's lists downloaded with it (getWrittenThroughForOrg), or, when none is known, the Sunday
// before the current one while `now`, the reader's instant (useReaderInstant), is before its Monday noon UTC. When that
// covers a passed Sunday, the planner has written the church down from the first of them through the last, which
// writePassedSundaysDown records, even when the write-down changes no list, for the change that follows writes
// something (issue #4209): the server then leaves those Sundays as the planner wrote them, and every screen reads them
// as stored. The write-down goes through writePlannerWrites, as applyPlannerEdit's does, so it reaches updateEach even
// with no list: that starts the song-list sync that sends the mark, which the change's own save (an org save for a
// repeat switch) would not; the pass waits until writePassedSundaysDown has stored the mark, and sends it inside the
// requests of the write-down's lists, or alone when there are none. Any other org writes nothing.
async function writeDownSundaysBegun(org: Org, allSongLists: AllSongLists, clock: { today: DateString; now: Date }) {
  if (org.type !== OrgType.Church) {
    return;
  }
  const writtenThrough = getWrittenThroughForOrg(org);
  const {
    changes: { update, remove },
    writtenDown,
  } = getWriteDownBeforeRowChange(org, allSongLists.toArray(), { ...clock, writtenThrough });
  // a removed list comes back with no entry, which updateEach deletes
  await writePlannerWrites(org, { lists: [...update, ...remove], writtenDown }, allSongLists.updateEach);
}

function DeleteRow({ allSongLists, rowIndex }: { allSongLists: AllSongLists; rowIndex: number }) {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const today = useCurrentDate();
  const getReaderNow = useReaderInstant();
  const row = currentOrg?.churchLiturgySchema?.rows.find(({ row }) => row === rowIndex);

  // the lists of the org's weekly type holding an entry at the row (a blank included), past and future alike: a
  // church's Sundays, which hold a repeating row's songs too (issue #4191), or another org's interval lists
  const getCountVerbiage = useCallback(() => {
    // a church's blank is no cell value; any other org's `text: ''` entry is one, as it always was
    const isChurch = currentOrg?.type === OrgType.Church;
    const entryCount = allSongLists.filter(
      (songList) =>
        songList.type === getWeeklySongListType(currentOrg) &&
        songList.songs.some((entry) => entry.row === rowIndex && !(isChurch && isBlankEntry(entry))),
    ).length;
    if (entryCount === 0) {
      return '';
    } else if (entryCount === 1) {
      return ' and one cell value';
    } else if (entryCount === 2) {
      return ' and both cell values';
    } else {
      return ` and all ${entryCount} cell values`;
    }
  }, [allSongLists, currentOrg, rowIndex]);

  // The confirmation counts what the Sundays hold before the write-down, which only records what they showed, so the
  // count stays as it was (issue #4192). The write-down runs under the schema without the row being deleted, whose
  // fill and blank moves deleteRowAt would erase at once; the other rows keep their numbers until the shift.
  const onClick = useCallback(async () => {
    const labelVerbiage = row?.label ? ` “${row.label}”` : '';
    const confirmed = await confirm({
      confirmation: (
        <span>
          Delete row{labelVerbiage}
          {getCountVerbiage()}?
        </span>
      ),
    });
    if (!confirmed) {
      return;
    }
    const schema = currentOrg?.churchLiturgySchema;
    if (currentOrg) {
      const withoutRow = schema && { ...schema, rows: schema.rows.filter(({ row }) => row !== rowIndex) };
      await writeDownSundaysBegun({ ...currentOrg, churchLiturgySchema: withoutRow }, allSongLists, {
        today,
        now: getReaderNow(),
      });
    }
    if (currentOrg && schema && schema.rows.length > 0) {
      await updateOrgInfo({
        id: currentOrg.id,
        churchLiturgySchema: {
          ...schema,
          rows: schema.rows
            .filter(({ row }) => row !== rowIndex) // filter out matching row
            .map((row) => (row.row >= rowIndex ? { ...row, row: row.row - 1 } : row)), // bump following rows up
        },
      });
    }
    await allSongLists.deleteRowAt(rowIndex);
  }, [allSongLists, currentOrg, getCountVerbiage, getReaderNow, row, rowIndex, today, updateOrgInfo]);

  return (
    <DeleteMenuEntryWrapper $isFirst={false} $isLast={true} $borderTop={true} onClick={onClick}>
      <TrashIconWrapper />
      Delete Row
    </DeleteMenuEntryWrapper>
  );
}

const DeleteMenuEntryWrapper = styled(MenuEntry)`
  color: var(--color-trash);
  fill: var(--color-trash);
`;

const TrashIconWrapper = styled(TrashIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
`;

const InsertBeforeIconWrapper = styled(WedgeUpIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

const InsertAfterIconWrapper = styled(WedgeDownIcon)`
  width: ${TypeIconWidth};
  height: ${TypeIconHeight};
  cursor: pointer;
  fill: var(--color-text);
`;

function getSchemaColumnTitle(church: Org | undefined) {
  return church?.type === OrgType.Church ? 'Sunday' : 'Date';
}

function getPlaceholderFromInterval(interval: IntervalType | undefined): string | undefined {
  if (!interval) {
    return;
  }
  switch (interval) {
    case IntervalType.Custom:
      return 'Hymn of the term';
    case IntervalType.Month:
      return 'Hymn of the month';
    case IntervalType.Week:
    case IntervalType.Year:
    case IntervalType.Season:
    case IntervalType.Fixed:
      return;
    default:
      ensureUnreachable(interval);
  }
}

const SchoolAssignmentSpeechBubble = styled(SpeechBubble)`
  left: 120%;
  top: -240%;
`;

const SchoolAssignmentTooltip = styled.div`
  width: 16ch;
  font-family: Jost, sans-serif;
  padding: 0.5rem 1rem;
  text-align: center;
`;
